import _ from 'lodash'
import { withCompPreviewInfo } from '@wix/editor-elements-integrations'
import { getTextPresets, ooiStyleParamsUtil, processOoiCss, isOOI, isRTL } from '@wix/thunderbolt-commons'
import { getColorValue, getSiteColors, getTpaFontParamValue, splitPropertiesValues } from './tpaStyleUtils'
import { getHeightInPixels, getWidthInPixels } from '@wix/editor-elements-common-utils'
import type { ColorMap, CompDefinition, StyleProperties, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import type { WidgetDataViewItem } from './TPAWidget'

type TPAWidgetDefinition = CompDefinition<never, WidgetDataViewItem, never, never>

const { shouldOverrideStyleParams, overrideStyleParams } = ooiStyleParamsUtil
const isPublicSegment = process.env.PACKAGE_NAME !== 'thunderbolt-ds'

export const resolveStyles = (siteFonts: ThemeFontMap, themeColors: ColorMap, rawStyleProperties: StyleProperties) => {
	const _siteFonts = _.values(siteFonts)
	const {
		color: colorParams,
		number: numberParams,
		boolean: booleanParams,
		font: fontParams,
		string: stringParams,
	} = splitPropertiesValues(rawStyleProperties)

	return {
		siteColors: getSiteColors(themeColors),
		siteTextPresets: getTextPresets(_siteFonts),
		style: {
			colors: _.mapValues(colorParams, (color, name) =>
				getColorValue(color, rawStyleProperties[`alpha-param_color_${name}`], themeColors)
			),
			numbers: _.mapValues(numberParams, Number),
			booleans: _.mapValues(booleanParams, (value) => value && value !== 'false'),
			fonts: _(fontParams)
				.mapValues((fontParam) => getTpaFontParamValue(fontParam, _siteFonts))
				.pickBy((v) => !!v)
				.value(),
			strings: stringParams,
			googleFontsCssUrl: '',
			uploadFontFaces: '',
		},
	}
}

export const css = withCompPreviewInfo<object, TPAWidgetDefinition>()(
	[
		'compId',
		'compData',
		'styleProperties',
		// @ts-ignore
		'formFactor',
		'language',
		'siteColors',
		'siteFonts',
		'compLayout',
		'hasResponsiveLayout',
		// @ts-ignore
		'anywhereThemeOverride',
		'compSingleLayout',
		'isOneDocMigrated',
		// @ts-ignore
		'isResponsive',
		...(process.env.PACKAGE_NAME === 'thunderbolt-ds'
			? (['componentViewMode', 'isLivePreviewOpenForEditor', 'isAdi'] as const)
			: []),
	],
	({
		compId,
		compData,
		styleProperties,
		// @ts-ignore
		formFactor,
		language,
		siteColors: themeColors,
		siteFonts,
		compLayout,
		hasResponsiveLayout,
		// @ts-ignore
		anywhereThemeOverride,
		componentViewMode,
		// @ts-ignore
		isLivePreviewOpenForEditor,
		// @ts-ignore
		isAdi,
		compSingleLayout,
		isOneDocMigrated,
		// @ts-ignore
		isResponsive,
		// @ts-ignore
		runAndReport,
	}) => {
		const shouldRenderAsOoi = isOOI({
			widgetSpec: compData.widgetSpecData,
			isMigratingToOoi: compData.isMigratingToOoi,
			componentViewMode,
			isLivePreviewOpenForEditor,
			isAdi,
		})

		const disableNoCssBundle =
			process.env.PACKAGE_NAME === 'thunderbolt-ds' &&
			!!compData.referenceId &&
			['621bc837-5943-4c76-a7ce-a0e38185301f', 'cc882051-73c9-41a6-8f90-f6ebc9f10fe1'].includes(compData.widgetId)

		if (!shouldRenderAsOoi || !compData.cssConfig || disableNoCssBundle) {
			return {}
		}

		const styleData = resolveStyles(siteFonts, themeColors, styleProperties)

		const { widgetId, appSettings, tpaData } = compData
		if (shouldOverrideStyleParams(widgetId)) {
			styleData.style = overrideStyleParams({
				styleParams: styleData.style,
				widgetId,
				appSettings,
				tpaData,
				isResponsive: hasResponsiveLayout,
				anywhereThemeOverride,
			})
		}

		// TODO: use isClassicComponent api when this PR merged: https://github.com/wix-private/thunderbolt/pull/33237
		const isClassicComponent = !hasResponsiveLayout
		const getClassicComponentWidth = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getWidthInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.docked?.left ? ('' as const) : (compLayout.width as number)
		}

		const getClassicComponentHeight = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getHeightInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.height as number
		}

		const dimensions = isClassicComponent
			? { width: getClassicComponentWidth(isOneDocMigrated), height: getClassicComponentHeight(isOneDocMigrated) }
			: { width: '' as const, height: '' as const }

		const cssString = processOoiCss({
			styleData,
			prefixSelector: `.${compId}`,
			shouldRemoveStaticCss: isPublicSegment,
			isRTL: isRTL(language),
			isMobile: formFactor === 'mobile',
			dimensions,
			cssConfig: compData.cssConfig,
			usesCssPerBreakpoint: !!compData.usesCssPerBreakpoint,
			runAndReport: runAndReport || ((fn) => fn()),
			widgetId,
			tpaData,
			isResponsive,
		})

		return cssString
	}
)
